import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Users, TrendingUp, CheckCircle2, Clock, ArrowUpRight, SlidersHorizontal, AlertTriangle, ShieldCheck, RefreshCw, XCircle, Trash2, MailOpen, Flame, PhoneCall, MousePointerClick, FilePlus, PieChart, Eye, ScanLine, FileText } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, fmtEur, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { NuovoPreventivoDialog } from "@/components/NuovoPreventivoDialog";

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hc, setHc] = useState({ last_health_check: null, overdue: false });
  const [hcResult, setHcResult] = useState(null);
  const [hcRunning, setHcRunning] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [recalcAll, setRecalcAll] = useState(false);
  const [reextractAll, setReextractAll] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    api.get("/admin/leads").then((r) => setLeads(r.data.leads || [])).finally(() => setLoading(false));
    api.get("/admin/health-check/status").then((r) => setHc(r.data)).catch(() => {});
  }, []);

  const runHealthCheck = async (ask) => {
    if (ask && !window.confirm("Vuoi eseguire ora la verifica settimanale di sistema?")) return;
    setHcRunning(true);
    try {
      const { data } = await api.post("/admin/health-check");
      setHcResult(data);
      setHc({ last_health_check: data.last_health_check, overdue: false });
      toast[data.overall === "ok" ? "success" : "error"](data.overall === "ok" ? "Sistema verificato: tutto funzionante ✓" : "Verifica: rilevati problemi, controlla i dettagli");
    } catch (e) {
      toast.error("Verifica non riuscita");
    } finally {
      setHcRunning(false);
    }
  };

  const completed = leads.filter((l) => l.status === "completed").length;
  const totalOpp = leads.reduce((s, l) => s + (l.eoi_opportunity || 0), 0);
  const hcDate = hc.last_health_check ? new Date(hc.last_health_check).toLocaleDateString("it-IT") : "mai";

  const fmtDT = (s) => { try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
  const hotLeads = leads
    .filter((l) => (l.email_tracking?.click_count > 0) || l.email_tracking?.status === "clicked")
    .sort((a, b) => new Date(b.email_tracking?.clicked_at || 0) - new Date(a.email_tracking?.clicked_at || 0));

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const provBreakdown = Object.entries(
    leads.reduce((acc, l) => {
      const k = (l.provenienza || "diretto");
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  const recalcAllLeads = async () => {
    if (!window.confirm("Ricalcolare TUTTI i lead con le regole e i parametri correnti (fotovoltaico, SEU, offerte)? L'operazione avviene in background.")) return;
    setRecalcAll(true);
    try {
      const { data } = await api.post("/admin/leads/recalculate-all");
      toast.success(`Ricalcolo di ${data.count} lead avviato in background`);
      setTimeout(() => api.get("/admin/leads").then((r) => setLeads(r.data.leads || r.data)).catch(() => {}), 15000);
    } catch {
      toast.error("Avvio ricalcolo non riuscito");
    } finally {
      setTimeout(() => setRecalcAll(false), 15000);
    }
  };

  const reextractAllLeads = async () => {
    if (!window.confirm("Ri-leggere TUTTI i lead con i motori aggiornati (Google Vision per scansioni/foto)?\n\nI lead con bollette in archivio vengono ri-letti; gli altri (storici, senza file salvato) vengono solo ricalcolati e segnalati per ricarica manuale. Operazione in background.")) return;
    setReextractAll(true);
    try {
      const { data } = await api.post("/admin/leads/reextract-all");
      toast.success(`Ri-estrazione avviata: ${data.with_bills}/${data.count} lead ri-leggibili con Google Vision`);
      setTimeout(() => api.get("/admin/leads").then((r) => setLeads(r.data.leads || r.data)).catch(() => {}), 20000);
    } catch {
      toast.error("Avvio ri-estrazione non riuscito");
    } finally {
      setTimeout(() => setReextractAll(false), 20000);
    }
  };

  const deleteLead = async (e, lead) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare definitivamente il lead "${lead.company?.ragione_sociale || "senza nome"}"? L'operazione non è reversibile.`)) return;
    setDeletingId(lead.id);
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      toast.success("Lead eliminato");
    } catch (err) {
      toast.error("Eliminazione non riuscita");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-400 hidden sm:block">{admin?.email}</span>
            <Link to="/admin/preventivi" data-testid="admin-preventivi-link" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-gold-400 transition-colors">
              <FileText className="h-4 w-4" /> Preventivi
            </Link>
            <Link to="/admin/settings" data-testid="admin-settings-link" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-gold-400 transition-colors">
              <SlidersHorizontal className="h-4 w-4" /> Offerte & Listino
            </Link>
            <button onClick={doLogout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-400 transition-colors">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading font-black text-3xl text-white tracking-tight">Lead & Audit</h1>
            <p className="text-slate-400 mt-1">Gestione dei check-up energetici raccolti dalla piattaforma.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setShowNew(true)} data-testid="new-preventivo-btn"
              className="h-10 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold">
              <FilePlus className="h-4 w-4 mr-1.5" /> Nuovo preventivo FV
            </Button>
            <Button onClick={recalcAllLeads} disabled={recalcAll} data-testid="recalculate-all-btn"
              className="h-10 rounded-xl bg-navy-600 hover:bg-navy-500 border border-gold-500/40 text-white font-semibold">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${recalcAll ? "animate-spin" : ""}`} /> {recalcAll ? "Ricalcolo in corso…" : "Ricalcola tutti"}
            </Button>
            <Button onClick={reextractAllLeads} disabled={reextractAll} data-testid="reextract-all-btn"
              className="h-10 rounded-xl bg-navy-600 hover:bg-navy-500 border border-gold-500/40 text-white font-semibold">
              <ScanLine className={`h-4 w-4 mr-1.5 ${reextractAll ? "animate-pulse" : ""}`} /> {reextractAll ? "Ri-estrazione…" : "Ri-estrai tutti (Vision)"}
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <StatCard icon={Users} label="Lead totali" value={leads.length} />
          <StatCard icon={CheckCircle2} label="Audit completati" value={completed} accent />
          <StatCard icon={TrendingUp} label="Opportunità aggregata (10 anni)" value={fmtEur(totalOpp)} accent />
        </div>

        {leads.length > 0 && (
          <div className="mt-6 card-dark rounded-2xl p-5" data-testid="provenienza-stats">
            <div className="flex items-center gap-2 mb-4">
              <PieChart className="h-5 w-5 text-gold-400" />
              <h2 className="font-heading font-bold text-lg text-white">Lead per provenienza</h2>
              <span className="text-xs text-slate-400 ml-1 hidden sm:inline">— da quale canale arrivano i clienti</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {provBreakdown.map(([src, count]) => {
                const pct = Math.round((count / leads.length) * 100);
                return (
                  <div key={src} data-testid={`prov-stat-${src}`}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 min-w-[9rem]">
                    <div>
                      <p className="text-white font-semibold capitalize leading-tight">{src}</p>
                      <p className="text-slate-500 text-[11px]">{pct}% del totale</p>
                    </div>
                    <span className="font-heading font-black text-2xl text-gold-400 ml-auto">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 card-dark rounded-2xl p-5" data-testid="health-check-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-gold-400" />
              <div>
                <p className="text-white font-heading font-bold">Verifica di sistema</p>
                <p className="text-slate-400 text-xs">Ultima verifica: <b className="text-slate-200">{hcDate}</b> · consigliata una volta a settimana</p>
              </div>
            </div>
            <Button onClick={() => runHealthCheck(true)} disabled={hcRunning}
              className="h-10 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold" data-testid="run-health-check-btn">
              {hcRunning ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
              {hcRunning ? "Verifica in corso…" : "Esegui verifica"}
            </Button>
          </div>

          {hc.overdue && !hcResult && (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2" data-testid="health-check-reminder">
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
              <span className="text-amber-200 text-xs">È passata almeno una settimana dall'ultima verifica. Vuoi eseguirla ora?</span>
            </div>
          )}

          {hcResult && (
            <div className="mt-4 space-y-2" data-testid="health-check-results">
              {hcResult.checks.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {c.status === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> : <XCircle className="h-4 w-4 text-red-400 shrink-0" />}
                  <span className="text-slate-200 font-medium">{c.name}:</span>
                  <span className="text-slate-400 text-xs">{c.detail}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {hotLeads.length > 0 && (
          <div className="mt-6 rounded-2xl border border-gold-500/40 bg-gradient-to-br from-gold-500/[0.08] to-transparent p-5" data-testid="hot-leads-section">
            <div className="flex items-center gap-2 mb-4">
              <Flame className="h-5 w-5 text-gold-400" />
              <h2 className="font-heading font-bold text-lg text-white">Lead caldi da richiamare</h2>
              <span className="text-[11px] font-bold text-gold-300 bg-gold-500/10 border border-gold-500/30 px-2 py-0.5 rounded-full">{hotLeads.length}</span>
              <span className="text-xs text-slate-400 ml-1 hidden sm:inline">— hanno cliccato il dossier (segnale d'acquisto forte)</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {hotLeads.map((l) => (
                <button key={l.id} onClick={() => navigate(`/admin/leads/${l.id}`)} data-testid={`hot-lead-${l.id}`}
                  className="text-left rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-gold-500/40 transition-colors p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-white font-semibold truncate">{l.company?.ragione_sociale}</p>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gold-300 bg-gold-500/10 border border-gold-500/30 px-1.5 py-0.5 rounded-full shrink-0">
                      <MousePointerClick className="h-3 w-3" /> {l.email_tracking?.click_count || 1}
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs mt-1">Cliccato il {fmtDT(l.email_tracking?.clicked_at)}</p>
                  <div className="flex items-center justify-between gap-2 mt-3">
                    {l.company?.telefono ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-emerald-300"><PhoneCall className="h-3.5 w-3.5" /> {l.company.telefono}</span>
                    ) : <span className="text-slate-500 text-xs">Tel. n.d.</span>}
                    {l.eoi_opportunity ? <span className="text-gold-400 text-sm font-mono-plex">{fmtEur(l.eoi_opportunity)}</span> : null}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 card-dark rounded-2xl overflow-hidden">
          <div className="grid-lines" />
          {loading ? (
            <div className="p-10 text-center text-slate-500">Caricamento…</div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-slate-500">Nessun lead ancora raccolto.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-white/8">
                    <th className="px-5 py-3 font-medium">Azienda</th>
                    <th className="px-5 py-3 font-medium">Settore</th>
                    <th className="px-5 py-3 font-medium">Prov.</th>
                    <th className="px-5 py-3 font-medium">Score</th>
                    <th className="px-5 py-3 font-medium">EOI (10y)</th>
                    <th className="px-5 py-3 font-medium">Priorità</th>
                    <th className="px-5 py-3 font-medium">Stato</th>
                    <th className="px-5 py-3 text-right font-medium">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {[...leads].sort((a, b) => (b.urgency_score ?? -1) - (a.urgency_score ?? -1)).map((l) => (
                    <motion.tr key={l.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => navigate(`/admin/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}>
                      <td className="px-5 py-3.5">
                        <p className="text-white font-medium flex items-center gap-1.5">
                          {l.company?.ragione_sociale}
                          {l.consumo_corretto && (
                            <span title={`Consumo auto-corretto su ${l.num_bollette_corrette} bolletta/e — verifica manuale consigliata`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-full"
                              data-testid={`consumo-corretto-badge-${l.id}`}>
                              <AlertTriangle className="h-3 w-3" /> Consumo corretto
                            </span>
                          )}
                          {l.urgency_score != null && (
                            <span title={`Urgency Score ${l.urgency_score}/100`}
                              data-testid={`urgency-badge-${l.id}`}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${l.urgency_band === "hot" ? "text-red-300 bg-red-500/10 border-red-500/30" : l.urgency_band === "warm" ? "text-amber-300 bg-amber-500/10 border-amber-500/30" : "text-blue-300 bg-blue-500/10 border-blue-500/30"}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${l.urgency_band === "hot" ? "bg-red-400" : l.urgency_band === "warm" ? "bg-amber-400" : "bg-blue-400"}`} /> {l.urgency_score}
                            </span>
                          )}
                          {(l.email_tracking?.status === "opened" || l.email_tracking?.status === "clicked") && (
                            <span title={`Dossier ${l.email_tracking.status === "clicked" ? "cliccato" : "aperto"} dal cliente${l.email_tracking.open_count ? ` (${l.email_tracking.open_count} aperture)` : ""}`}
                              data-testid={`email-opened-badge-${l.id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border text-emerald-300 bg-emerald-500/10 border-emerald-500/30">
                              <MailOpen className="h-3 w-3" /> {l.email_tracking.status === "clicked" ? "Click" : "Aperta"}
                            </span>
                          )}
                          {l.report_view?.count > 0 && (
                            <span title={`Report aperto dal cliente${l.report_view.count > 1 ? ` (${l.report_view.count} volte)` : ""}${l.report_view.last_at ? ` · ultima: ${fmtDT(l.report_view.last_at)}` : ""}`}
                              data-testid={`report-viewed-badge-${l.id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border text-sky-300 bg-sky-500/10 border-sky-500/30">
                              <Eye className="h-3 w-3" /> Report aperto{l.report_view.count > 1 ? ` ×${l.report_view.count}` : ""}
                            </span>
                          )}
                        </p>
                        <p className="text-slate-500 text-xs">{l.company?.email}</p>
                        {l.provenienza && l.provenienza !== "diretto" && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-violet-300 bg-violet-500/10 border border-violet-500/30 px-1.5 py-0.5 rounded-full" data-testid={`provenienza-badge-${l.id}`} title="Provenienza del lead">
                            {l.provenienza}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-slate-400">{l.company?.settore}</td>
                      <td className="px-5 py-3.5 text-slate-400">{l.company?.provincia}</td>
                      <td className="px-5 py-3.5 font-mono-plex text-slate-200">{l.efficiency_score ?? "–"}</td>
                      <td className="px-5 py-3.5 font-mono-plex text-gold-400">{l.eoi_opportunity ? fmtEur(l.eoi_opportunity) : "–"}</td>
                      <td className="px-5 py-3.5 font-mono-plex text-slate-200">{l.eoi_priority != null ? `${fmtNum(l.eoi_priority, 1)}/10` : "–"}</td>
                      <td className="px-5 py-3.5">
                        {l.status === "completed" ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gold-300 bg-gold-500/10 px-2 py-1 rounded-full"><CheckCircle2 className="h-3 w-3" /> Completo</span>
                        ) : l.status === "manual" ? (
                          <span className="inline-flex items-center gap-1 text-xs text-sky-300 bg-sky-500/10 px-2 py-1 rounded-full" data-testid={`status-manual-${l.id}`}><FilePlus className="h-3 w-3" /> Preventivo</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-300 bg-amber-500/10 px-2 py-1 rounded-full"><Clock className="h-3 w-3" /> Preliminare</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <ArrowUpRight className="h-4 w-4 text-slate-500" />
                          <button
                            onClick={(e) => deleteLead(e, l)}
                            disabled={deletingId === l.id}
                            title="Elimina lead"
                            data-testid={`delete-lead-btn-${l.id}`}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                            {deletingId === l.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <NuovoPreventivoDialog open={showNew} onOpenChange={setShowNew} />
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="card-dark rounded-2xl p-5">
    <Icon className={`h-5 w-5 mb-3 ${accent ? "text-gold-400" : "text-slate-400"}`} />
    <p className="text-slate-400 text-xs uppercase tracking-wider">{label}</p>
    <p className="font-heading font-bold text-2xl text-white mt-1">{value}</p>
  </div>
);
